import type { CurrencyCodeEnum } from "src/api/enums/CurrencyCodeEnum";
import type { CurrencySymbolEnum } from "src/api/enums/CurrencySymbolEnum";
import type { TFunction } from "src/design-system/core/TranslationContext";
import type { MarketingAction } from "src/types/action/MarketingAction";
import type { MarketingActionScenario } from "src/types/action/MarketingActionScenario";
import type { Channel } from "src/types/channel";
import type { LanguageEnum } from "src/types/enum/LanguageEnum";
import type { ThirdServiceCategory } from "src/types/posCredential";
import { MarketingActionActivationTypeEnum } from "src/utils/activationTypes";
import { OFFER_TRANSLATION_NAMESPACE } from "src/views/offer/config";
import { OFFER_PERFORMANCE_TAB_TK } from "src/views/offer/offer-tables-view/config";
import { SPONSORED_AD_TRANSLATION_NAMESPACE } from "src/views/sponsored-ads/config";

const SPONSORED_AD_COMMON_TAB_TK = "SPONSORED_AD_COMMON_TAB_TK";
const OFFER_TABLE_COMMON_TK = `${OFFER_TRANSLATION_NAMESPACE}:${OFFER_PERFORMANCE_TAB_TK}`;
export interface FormatterOptions {
    translateRaw: TFunction;
    translateCommon: TFunction;
    currencyCode: CurrencyCodeEnum;
    currencySymbol: CurrencySymbolEnum;
    decimalSymbol: string;
    language: LanguageEnum;
}

export function formatPoss(poss: { name: string }[]): string {
    return poss
        .map((pos) => pos.name)
        .sort((a, b) => a.localeCompare(b))
        .join(", ");
}

export function extendMarketingActionWithMainScenario<
    T extends {
        scenario?: MarketingActionScenario[];
        scenarios: MarketingActionScenario[];
        duoMarketingAction?: MarketingAction;
        activationTypes?: MarketingActionActivationTypeEnum[];
    },
>(action: T): T | null {
    if (!action) {
        return null;
    }

    const isSmartScheduler =
        (action?.activationTypes &&
            action.activationTypes.includes(MarketingActionActivationTypeEnum.SmartScheduler)) ??
        false;

    if (!action.duoMarketingAction) {
        return {
            ...action,
            mainScenario:
                (action.scenarios || action.scenario)?.length === 1 && !isSmartScheduler
                    ? (action.scenarios || action.scenario)[0]
                    : undefined,
        };
    }

    const obj = extendMarketingActionWithMainScenario(action.duoMarketingAction);
    return {
        ...action,
        mainScenario:
            (action.scenarios || action.scenario)?.length === 1 && !isSmartScheduler
                ? (action.scenarios || action.scenario)[0]
                : undefined,
        duoMarketingAction: obj,
    };
}

export function formatServiceList(
    serviceList: ThirdServiceCategory[],
    options: FormatterOptions
): string {
    const { translateRaw } = options;

    return translateRaw(
        `${SPONSORED_AD_TRANSLATION_NAMESPACE}:${SPONSORED_AD_COMMON_TAB_TK}.service-list-length`,
        {
            serviceListLength: serviceList.length,
        }
    );
}

export function formatChannelList(channelList: Channel[], options: FormatterOptions): string {
    const { translateRaw } = options;

    return translateRaw(`${OFFER_TABLE_COMMON_TK}.channel-list-length`, {
        channelListLength: channelList.length,
    });
}
